import { Link } from '@tanstack/react-router';
import { BookOpen } from 'lucide-react';
import { SUBJECTS } from '@jeraisonne/db';
import { AvatarCircle } from '@/components/avatar-circle';
import { Spinner } from '@/components/spinner';
import { formatRelativeDate } from './format';
import type { AssignmentRow, AuthorsById, ViewerRole } from './types';

const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));

function buildAuthorLine(role: ViewerRole, tutorName: string, studentName: string | null): string {
  if (role === 'student') return ' t’a donné un devoir';
  if (role === 'parent') return ` a donné un devoir à ${studentName ?? 'ton enfant'}`;
  // tutor : son propre devoir
  return studentName ? ` → devoir pour ${studentName}` : ' a donné un devoir';
}

export function AssignmentCard({
  data,
  viewerRole,
  authorsById,
  isDone = false,
  onStart,
  isStarting = false,
  startDisabled = false,
}: {
  data: AssignmentRow;
  viewerRole: ViewerRole;
  authorsById: AuthorsById;
  isDone?: boolean;
  onStart?: () => void;
  /** Ouverture en cours après le clic : spinner + bouton désactivé. */
  isStarting?: boolean;
  /** Un autre devoir est en cours d'ouverture. */
  startDisabled?: boolean;
}) {
  const chapter = data.exercises?.chapters;
  const thumbnailUrl = data.exercises?.illustration_url ?? chapter?.illustration_url ?? null;
  const isOverdue = data.due_at !== null && new Date(data.due_at).getTime() < Date.now();
  const isDueSoon =
    !isOverdue && data.due_at !== null && new Date(data.due_at).getTime() < Date.now() + 86_400_000;

  const tutorName = data.profiles?.display_name ?? (data.class_id ? 'Ma classe' : 'Le prof');
  const tutorAvatar = data.profiles?.avatar_url ?? null;
  const studentInfo = authorsById.get(data.student_id);
  const studentName = studentInfo?.display_name ?? null;

  // En vue tutor, l'auteur affiché en haut de carte = le tutor (lui-même).
  // En vue student/parent, l'auteur affiché = le tutor qui a donné le devoir.
  const headerName = tutorName;
  const headerAvatar = tutorAvatar;
  const authorLine = buildAuthorLine(viewerRole, tutorName, studentName);

  return (
    <div className="overflow-hidden rounded-2xl bg-white">
      <div className="flex items-center gap-2.5 px-4 py-3">
        {data.tutor_id ? (
          <Link
            to="/tutors/$tutorId"
            params={{ tutorId: data.tutor_id }}
            className="flex min-w-0 flex-1 items-center gap-2.5"
          >
            <AvatarCircle name={headerName} src={headerAvatar} size="sm" />
            <div className="min-w-0 flex-1">
              <span className="text-ink-900 font-sans text-xs font-semibold hover:underline">
                {headerName}
              </span>
              <span className="text-ink-500 font-sans text-xs">{authorLine}</span>
            </div>
          </Link>
        ) : (
          <>
            <AvatarCircle name={headerName} src={headerAvatar} size="sm" />
            <div className="min-w-0 flex-1">
              <span className="text-ink-900 font-sans text-xs font-semibold">{headerName}</span>
              <span className="text-ink-500 font-sans text-xs">{authorLine}</span>
            </div>
          </>
        )}
        <span className="text-ink-400 shrink-0 font-sans text-[11px]">
          {formatRelativeDate(data.assigned_at)}
        </span>
      </div>

      <div className="flex gap-3 p-4">
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-1.5">
              <BookOpen className="text-primary-400 h-3.5 w-3.5 shrink-0" />
              <span className="text-primary-600 font-sans text-[11px] font-semibold uppercase tracking-wider">
                Devoir
              </span>
              <span
                className={[
                  'ml-auto shrink-0 rounded-full px-2.5 py-0.5 font-sans text-[11px] font-semibold',
                  isDone
                    ? 'bg-green-100 text-green-700'
                    : data.status === 'in_progress'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-ink-100 text-ink-500',
                ].join(' ')}
              >
                {isDone ? 'Fait' : data.status === 'in_progress' ? 'En cours' : 'À faire'}
              </span>
            </div>
            <p className="text-ink-900 font-sans text-sm font-semibold leading-snug">
              {data.exercises?.title ?? 'Exercice'}
            </p>
            {chapter && (
              <p className="text-ink-600 mt-0.5 font-sans text-xs">
                {SUBJECT_LABEL[chapter.subject] ?? chapter.subject} · {chapter.title}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between gap-2">
            <div>
              {data.due_at && (
                <p
                  className={[
                    'font-sans text-xs font-semibold',
                    isOverdue ? 'text-red-500' : isDueSoon ? 'text-amber-600' : 'text-ink-500',
                  ].join(' ')}
                >
                  {isOverdue ? 'En retard · ' : isDueSoon ? 'Bientôt · ' : 'Pour le '}
                  {new Date(data.due_at).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'short',
                  })}
                </p>
              )}
            </div>
            {viewerRole === 'student' && onStart && (
              <button
                onClick={onStart}
                disabled={isStarting || startDisabled}
                aria-busy={isStarting || undefined}
                className={[
                  'inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-xl px-4 py-1.5 font-sans text-xs font-semibold transition-all active:translate-y-0.5 disabled:opacity-70',
                  isDone
                    ? 'bg-[linear-gradient(180deg,#f7d068,#f4c64e)] text-[#0a2e57] shadow-[0_3px_0_#c79a26] hover:bg-[linear-gradient(180deg,#f4c64e,#e7b431)] active:shadow-[0_1px_0_#c79a26]'
                    : 'bg-primary-400 hover:bg-primary-500 text-white',
                ].join(' ')}
              >
                {isStarting && <Spinner />}
                {isStarting
                  ? 'Ouverture…'
                  : isDone
                    ? 'Recommencer'
                    : data.status === 'in_progress'
                      ? 'Continuer'
                      : 'Commencer'}
              </button>
            )}
          </div>
        </div>

        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            loading="lazy"
            aria-hidden="true"
            className="bg-ink-50 h-20 w-20 shrink-0 rounded-xl object-cover sm:h-24 sm:w-24"
          />
        ) : (
          <div
            aria-hidden="true"
            className="from-primary-100 to-primary-300 bg-linear-to-br flex h-20 w-20 shrink-0 items-center justify-center rounded-xl sm:h-24 sm:w-24"
          >
            <BookOpen className="text-primary-500 h-6 w-6" />
          </div>
        )}
      </div>
    </div>
  );
}
